/* eslint-disable no-unused-vars */
import {createRxDatabase} from 'rxdb';
import {RxDatabase} from 'rxdb/dist/types/types';
import {getRxStorageLoki} from 'rxdb/plugins/lokijs';
import {AlertDataSource, PinDataSource, RouteDataSource, ServiceDataSource, ServiceGroupDataSource, StopDataSource} from './data-sources';
import {alertSchema, routeSchema} from './schemas';
import {pinSchema} from './schemas/pin.schema';
import {serviceGroupSchema} from './schemas/service-group.schema';
import {serviceSchema} from './schemas/service.schema';
import {stopSchema} from './schemas/stop.schema';
import {recentSearchesSchema} from '../../services/data/datasources/recentSearches/recentSearches.schema';
import {SearchDataSource} from './data-sources/search.data-source';
import {searchSchema} from './schemas/search.schema';
import {getRxStorageDexie} from 'rxdb/plugins/dexie';
import {ShapesDataSource} from './data-sources/shapes.data-source';
import {shapeSchema} from './schemas/shape.schema';
import {timetableCacheSchema} from './schemas/timetable-cache.schema';
import {TimetableCacheDataSource} from './data-sources/timetable-cache.data-source';

// addPouchPlugin(require('pouchdb-adapter-idb'));
const LokiIncrementalIndexedDBAdapter = require('lokijs/src/incremental-indexeddb-adapter');

export class Db {

    private db?: RxDatabase;
    public dataSources: {
        alerts: AlertDataSource,
        // eslint-disable-next-line camelcase
        searches: SearchDataSource,
        routes: RouteDataSource,
        stops: StopDataSource,
        serviceGroups: ServiceGroupDataSource,
        services: ServiceDataSource,
        pins: PinDataSource,
        shapes: ShapesDataSource,
        timetableCache: TimetableCacheDataSource,
    } | undefined;

    constructor() {

    }

    public async init(): Promise<Db> {
        await this.createDatabase();
        await this.createCollections();
        this.createDataSources();

        // Limpieza inicial de timetables al arrancar
        await this.dataSources?.timetableCache.removeExpiredEntries?.();

        const oneDayInMs = 24 * 60 * 60 * 1000;
        setInterval(() => {
            this.dataSources?.timetableCache.removeExpiredEntries?.();
        }, oneDayInMs);

        await this.dataSources?.timetableCache.removeExpiredEntries?.();
        return this;
    }

    private async createDatabase(): Promise<RxDatabase> {
        if (!this.db) {
            this.db = await createRxDatabase({
                name: 'amb',
                storage: getRxStorageDexie(),
                multiInstance: false,
                eventReduce: true,
                ignoreDuplicate: true,
            });
        }

        return this.db;
    }

    private async createCollections(): Promise<any> {

        return this.db?.addCollections(
            {
                alerts: {
                    schema: alertSchema,
                },
                searches: {
                    schema: searchSchema,
                },
                timetablecache: {
                    schema: timetableCacheSchema,
                    migrationStrategies: {
                        1: (oldDoc: any) => {
                            return {
                                id: `${oldDoc.stop_id}::${oldDoc.route_id || ''}`,
                                stop_id: oldDoc.stop_id,
                                route_id: oldDoc.route_id || '',
                                itineraries_timetables: oldDoc.timetable_data || [],
                                fetched_at: oldDoc.fetched_at || Date.now(),
                            };
                        },
                    },
                },
                routes: {
                    schema: routeSchema,
                    migrationStrategies: {
                        1: (oldDoc) => {
                            return {
                                ...oldDoc,
                                mapUrl: oldDoc.mapUrl || '',
                                timetableUrl: oldDoc.timetableUrl || '',
                            };
                        },
                    },
                },
                stops: {
                    schema: stopSchema,
                },
                servicegroups: {
                    schema: serviceGroupSchema,
                },
                services: {
                    schema: serviceSchema,
                    migrationStrategies: {
                        // Define la función de migración para la versión 0 a la versión 1
                        1: function(doc) {
                            // Copia el documento existente
                            const migratedDoc = {...doc};

                            // Realiza la migración específica para pasar de la versión 0 a la versión 1:
                            // Se añade el campo web_url si no existe
                            if (!migratedDoc.web_url) {
                                migratedDoc.web_url = '';
                            }

                            // Devuelve el documento migrado
                            return migratedDoc;
                        },
                    },
                },
                pins: {
                    schema: pinSchema,
                },
                shapes: {
                    schema: shapeSchema,
                },
            },
        );
    }

    private createDataSources(): Db {
        if (this.db) {
            this.dataSources = {
                alerts: new AlertDataSource(this.db.alerts),
                searches: new SearchDataSource(this.db.searches),
                routes: new RouteDataSource(this.db.routes),
                stops: new StopDataSource(this.db.stops),
                serviceGroups: new ServiceGroupDataSource(this.db.servicegroups),
                services: new ServiceDataSource(this.db.services),
                pins: new PinDataSource(this.db.pins),
                shapes: new ShapesDataSource(this.db.shapes),
                timetableCache: new TimetableCacheDataSource(this.db.timetablecache),
            };
        }
        return this;
    }
}
